import React, { useState } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Link,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useDialogStyles, useFormFieldStyles, useSectionStyles } from '@/styles/utilities/hooks';
import logoLight from '../../../images/logo_light.png';

export default function UserLogin({ onLogin, onRegisterClick }) {
  const theme = useTheme();
  const dialogStyles = useDialogStyles({ 
    maxWidth: theme.spacing(55),
    minWidth: theme.spacing(45)  // Ensure proportional scaling
  });
  const formFieldStyles = useFormFieldStyles();
  const sectionStyles = useSectionStyles();
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    setError('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');

    try {
      await onLogin(formData);
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Invalid username or password');
      } else if (err.response?.status === 403) {
        setError('Account not approved yet. Please wait for admin approval.');
      } else {
        setError(err.response?.data?.detail || 'Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flex: 1,
        backgroundColor: theme.palette.grey[100],
        padding: theme.spacing(2)
      }}
    >
      <Paper
        elevation={3}
        sx={{
          ...dialogStyles.paper,
          padding: theme.spacing(5),
          borderRadius: theme.spacing(1)
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center',
          gap: theme.spacing(2),
          mb: theme.spacing(3)
        }}>
          <img 
            src={logoLight} 
            alt="Protify Logo" 
            style={{ 
              height: theme.spacing(10),
              objectFit: 'contain'
            }} 
          />
          <Typography sx={{ 
            fontSize: theme.typography.fontSize.base,
            color: theme.palette.text.secondary 
          }}>
            Welcome to Protify
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: theme.spacing(2) }}>
            {error}
          </Alert>
        )}

        <Box 
          component="form" 
          onSubmit={handleSubmit}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: theme.spacing(2.5)
          }}
        >
          <TextField
            label="Username"
            value={formData.username}
            onChange={handleChange('username')}
            required
            autoFocus
            sx={{
              ...formFieldStyles.input,
              width: theme.spacing(38)
            }}
          />
          
          <TextField
            label="Password"
            type="password"
            value={formData.password}
            onChange={handleChange('password')}
            required
            sx={{
              ...formFieldStyles.input,
              width: theme.spacing(38)
            }}
          />

          <Button
            type="submit"
            variant="contained"
            size="medium"
            disabled={loading}
            sx={{ 
              width: theme.spacing(38),
              mt: theme.spacing(1),
              textTransform: 'none',
              height: theme.dimensions.button.medium
            }}
          >
            {loading ? <CircularProgress size={theme.spacing(3)} color="inherit" /> : 'Sign In'}
          </Button>

          <Box sx={{ textAlign: 'center' }}>
            <Typography sx={{ 
              fontSize: theme.typography.fontSize.base,
              color: theme.palette.text.secondary 
            }}>
              Don't have an account?{' '}
              <Link
                onClick={(e) => {
                  e.preventDefault();
                  onRegisterClick && onRegisterClick();
                }}
                sx={{ 
                  textDecoration: 'none',
                  fontWeight: theme.typography.fontWeight.medium,
                  cursor: 'pointer'
                }}
              >
                Register here
              </Link>
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}
